import { OrderItemAccessoriesInfo, OrderPaymentStatus } from '@leid/api-orders'
import { createAsyncThunk, createSlice, ThunkDispatch } from '@reduxjs/toolkit'
import i18n from 'i18next'

import { trackAddToCart } from 'app/components/shared/Analytics/segment'
import {
  AddOrderItemToOrder,
  AddProductToOrderOptions,
  Order,
  OrderInfo,
  OrderItem,
  OrderItemInfo,
  UpdateOrder,
  UpdateOrderItemInOrder,
  UpdateProductInOrderOptions,
} from 'appTypes/orders'
import apiClient from 'helpers/clients/apiClient'
import { AppThunk } from 'store'
import { RootState } from 'store/rootReducer'
import { STORAGE_KEYS } from 'utils/constants'
import { getFromStorage, removeFromStorage, saveToStorage } from 'utils/storage'
import { displaySnackbar } from '../snackbar/snackbarSlice'
import { getIsUserAuthenticated } from 'app/utils/cognito/cognitoActions'

export type ProlongationData = {
  orderItem: {
    id: number
  } | null
  confirmed: boolean
  priceChange: number
  id: number
}

interface InitialState {
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
  order: OrderInfo | null
  prolongation: OrderInfo | null
  reconciliation: OrderInfo | null
  currentProductPrice: number
  cart: OrderInfo | null
  complementaryPrice: number
  isProductAddedToOrder: boolean
  orderItem: OrderItemInfo | null
  prolongationData: null | ProlongationData
}

const initialState: InitialState = {
  isLoading: false,
  isError: false,
  isSuccess: false,
  order: null,
  prolongation: null,
  reconciliation: null,
  currentProductPrice: 0,
  cart: null,
  complementaryPrice: 0,
  isProductAddedToOrder: false,
  orderItem: null,
  prolongationData: null,
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      state.isError = false
    },
    requestFailure: (state) => {
      state.isLoading = false
      state.isError = false
    },
    requestSuccess: (state) => {
      state.isLoading = false
      state.isSuccess = true
    },
    resetSuccess: (state) => {
      state.isSuccess = false
    },
    getCartSuccess: (state, action) => {
      state.isLoading = false
      state.cart = action.payload
    },
    resetCart: (state) => {
      state.cart = null
    },

    getOrderSuccess: (state, action) => {
      state.isLoading = false
      state.order = action.payload
    },
    resetOrder: (state) => {
      state.order = null
    },

    getProlongationSuccess: (state, action) => {
      state.isLoading = false
      state.prolongation = action.payload
    },
    getReconciliationSuccess: (state, action) => {
      state.isLoading = false
      state.reconciliation = action.payload
    },
    resetProlongation: (state) => {
      state.prolongation = null
    },

    setCurrentProductPrice: (state, action) => {
      state.currentProductPrice = action.payload
    },
    resetCurrentProductPrice: (state) => {
      state.currentProductPrice = 0
    },

    addToComplementaryPrice: (state, action) => {
      state.complementaryPrice += action.payload
    },
    resetComplementaryPrice: (state) => {
      state.complementaryPrice = 0
    },

    productAddedToOrderSuccess: (state) => {
      state.isProductAddedToOrder = true
    },
    resetProductAddedToOrder: (state) => {
      state.isProductAddedToOrder = false
    },

    getOrderItemSuccess: (state, action) => {
      state.orderItem = action.payload
    },
    resetOrderItem: (state) => {
      state.orderItem = null
    },

    setProlongationData: (state, action) => {
      state.prolongationData = action.payload
    },
    resetProlongationData: (state) => {
      state.prolongationData = null
    },
  },
})

export const {
  requestStart,
  requestFailure,
  requestSuccess,
  getOrderSuccess,
  getProlongationSuccess,
  getReconciliationSuccess,
  resetCart,
  getCartSuccess,
  resetSuccess,
  resetOrder,
  resetProlongation,
  setCurrentProductPrice,
  resetCurrentProductPrice,
  addToComplementaryPrice,
  resetComplementaryPrice,
  productAddedToOrderSuccess,
  resetProductAddedToOrder,
  getOrderItemSuccess,
  setProlongationData,
  resetProlongationData,
  resetOrderItem,
} = cartSlice.actions

export default cartSlice.reducer

export const createNewOrder =
  (managerId?: number): AppThunk<Promise<number | undefined>> =>
  async (dispatch): Promise<number | undefined> => {
    dispatch(requestStart())

    try {
      const response = await apiClient.createOrder({
        managerId: managerId,
      })
      if (!response.success || !response.data) {
        dispatch(requestFailure())
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return
      }

      const order = response.data

      saveToStorage(STORAGE_KEYS.orderId, order.id)
      saveToStorage(STORAGE_KEYS.orderToken, order.token)
      const isAuthenticated = await getIsUserAuthenticated()
      if (isAuthenticated) {
        await dispatch(connectOrderToAccount(order.id))
      }
      dispatch(requestSuccess())

      return order.id
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
    return undefined
  }

export const addProductToOrder = createAsyncThunk<
  OrderItemInfo | undefined,
  AddProductToOrderOptions,
  { state: RootState }
>('addProductToOrder', async (addProductToOrderOptions, { dispatch, rejectWithValue }) => {
  dispatch(requestStart())
  const orderToken = JSON.parse(getFromStorage(STORAGE_KEYS.orderToken))

  if (!addProductToOrderOptions.orderId) {
    return undefined
  }

  try {
    const createRequest: AddOrderItemToOrder = {
      productId: addProductToOrderOptions.itemId,
      orderId: addProductToOrderOptions.orderId,
      amount: addProductToOrderOptions.amount,
      regionId: addProductToOrderOptions.regionId,
      locationId: addProductToOrderOptions.locationId,
      rentedFrom: addProductToOrderOptions.rentedFrom,
      rentedTo: addProductToOrderOptions.rentedTo,
      orderToken: orderToken,
      resignFromCleaning: addProductToOrderOptions.resignFromCleaning,
      resignFromInsurance: addProductToOrderOptions.resignFromInsurance,
      resignFromDeductibles: addProductToOrderOptions.resignFromDeductibles,
    }

    const accessories = addProductToOrderOptions.complementaryItems
    if (accessories) {
      const accessoriesRequest: OrderItemAccessoriesInfo[] = []

      for (const [key, amount] of Object.entries(accessories)) {
        accessoriesRequest.push({
          productId: Number(key),
          amount: amount,
          // TODO: orderItemId:  For prolongation populate this
        })
      }

      createRequest.accessories = accessoriesRequest
    }

    const orderItem = await createOrderItem(createRequest, addProductToOrderOptions.pathname, dispatch)
    if (!orderItem) {
      return rejectWithValue(undefined)
    }

    dispatch(productAddedToOrderSuccess())
    dispatch(getCart(addProductToOrderOptions.orderId))

    return orderItem
  } catch {
    dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    dispatch(requestFailure())
    return rejectWithValue(undefined)
  }
})

export const updateProductInOrder = createAsyncThunk<
  OrderItemInfo | undefined,
  UpdateProductInOrderOptions,
  { state: RootState }
>('updateProductInOrder', async ({ orderItemId, complementaryItems, ...options }, { dispatch, rejectWithValue }) => {
  dispatch(requestStart())

  if (!options.orderId) {
    return undefined
  }

  try {
    const updateRequest: UpdateOrderItemInOrder = {
      amount: options.amount,
      regionId: options.regionId,
      locationId: options.locationId,
      rentedFrom: options.rentedFrom,
      rentedTo: options.rentedTo,
      resignFromCleaning: options.resignFromCleaning,
      resignFromInsurance: options.resignFromInsurance,
      resignFromDeductibles: options.resignFromDeductibles,
    }

    if (complementaryItems) {
      const accessoriesRequest: OrderItemAccessoriesInfo[] = []

      for (const [key, amount] of Object.entries(complementaryItems)) {
        accessoriesRequest.push({
          productId: Number(key),
          amount: amount,
          // TODO: orderItemId:  For prolongation populate this
        })
      }

      updateRequest.accessories = accessoriesRequest
    }

    const orderItemInfo = await updateOrderItem(orderItemId, updateRequest, options.pathname, dispatch)
    if (!orderItemInfo) {
      return rejectWithValue(undefined)
    }

    dispatch(productAddedToOrderSuccess())
    dispatch(getCart(options.orderId))

    return orderItemInfo
  } catch {
    dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    dispatch(requestFailure())
    return rejectWithValue(undefined)
  }
})

export const deleteProductFromOrder =
  (orderItemId: number): AppThunk =>
  async (dispatch) => {
    const orderId = Number(localStorage.getItem('orderId'))
    const shouldDisplayLoading = false

    try {
      const response = await apiClient.deleteOrderItem(orderItemId)
      if (!response.success) {
        dispatch(requestFailure())
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return
      }

      dispatch(getCart(orderId, shouldDisplayLoading))
      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const connectOrderToAccount =
  (orderId: number): AppThunk<Promise<string>> =>
  async (dispatch): Promise<string> => {
    const orderToken = JSON.parse(getFromStorage(STORAGE_KEYS.orderToken))

    dispatch(requestStart())

    try {
      const response = await apiClient.connectOrderToAccount(orderId, orderToken)
      if (!response.success) {
        dispatch(requestFailure())

        if (response.error?.errors.some((s) => s.title === 'Order unavailable')) {
          dispatch(displaySnackbar({ message: i18n.t('common:productsNoLongerAvailable'), isError: true }))
          return '/browse'
        }
        if (response.error?.errors.some((s) => s.title === 'Some order items unavailable')) {
          dispatch(displaySnackbar({ message: i18n.t('common:productsNoLongerAvailable'), isError: true }))
          return '/cart'
        }

        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return ''
      }

      if (!response.data) {
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return ''
      }

      removeFromStorage(STORAGE_KEYS.orderToken)
      dispatch(requestSuccess())
      return ''
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }

    return ''
  }

export const sortOrderItems = (order: Order): Order => {
  const sortedOrderItems = order.orderItems.sort((a: OrderItem, b: OrderItem) => b.id - a.id)
  return {
    ...order,
    orderItems: sortedOrderItems,
  }
}

export const sortOrderItemsFromModule = (orderInfo: OrderInfo): OrderInfo => {
  const sortedOrderItems = orderInfo.orderItems.sort(
    (a: OrderItemInfo, b: OrderItemInfo) => b.orderItem.id - a.orderItem.id,
  )
  return {
    ...orderInfo,
    orderItems: sortedOrderItems,
  }
}

const filterCanceledOrderItemsFromOrder = (orderInfo: OrderInfo): OrderInfo => {
  const filteredOrderItems = orderInfo.orderItems.filter(
    (s) =>
      s.orderItem.status !== OrderPaymentStatus.canceled && s.orderItem.status !== OrderPaymentStatus.paidAndCanceled,
  )
  return {
    ...orderInfo,
    orderItems: filteredOrderItems,
  }
}

export const getCart =
  (id: string | number, shouldDisplayLoading = true): AppThunk =>
  async (dispatch) => {
    if (shouldDisplayLoading) {
      dispatch(requestStart())
    }
    const orderToken = JSON.parse(getFromStorage(STORAGE_KEYS.orderToken))
    const isAuthenticated = await getIsUserAuthenticated()

    try {
      if (orderToken || isAuthenticated) {
        const response = await apiClient.getOrder(id, orderToken)
        if (!response.success) {
          dispatch(requestFailure())
          dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
          return
        }

        if (!response.data) {
          removeFromStorage(STORAGE_KEYS.orderId)
          dispatch(resetCart())
          dispatch(requestFailure())
          return
        }

        const orderInfo = response.data

        // In cart we allow only to have started or paymentStarted orders
        if (
          orderInfo.order.status !== OrderPaymentStatus.started &&
          orderInfo.order.status !== OrderPaymentStatus.paymentStarted
        ) {
          removeFromStorage(STORAGE_KEYS.orderId)
          dispatch(resetCart())
          dispatch(requestFailure())
          return
        }

        const filteredOrderInfo = filterCanceledOrderItemsFromOrder(orderInfo)
        const sortedOrderInfo = sortOrderItemsFromModule(filteredOrderInfo)

        dispatch(getCartSuccess(sortedOrderInfo))
      }

      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const getOrder =
  (id: string | number, discardCancelledOrder = false): AppThunk =>
  async (dispatch) => {
    dispatch(requestStart())

    try {
      const response = await apiClient.getOrder(id)
      if (!response.success || !response.data) {
        dispatch(requestFailure())
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return
      }

      const orderInfo = response.data

      if (
        discardCancelledOrder &&
        (orderInfo.order.status === OrderPaymentStatus.canceled ||
          orderInfo.order.status === OrderPaymentStatus.paidAndCanceled)
      ) {
        removeFromStorage(STORAGE_KEYS.orderId)
        dispatch(resetOrder())
        dispatch(resetCart())
        dispatch(requestFailure())
        return
      }

      const sortedOrder = sortOrderItemsFromModule(orderInfo)

      dispatch(getOrderSuccess(sortedOrder))
      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const getOrderNoRequest =
  (orderInfo: OrderInfo, discardCancelledOrder = false): AppThunk =>
  async (dispatch) => {
    dispatch(requestStart())

    try {
      if (
        discardCancelledOrder &&
        (orderInfo.order.status === OrderPaymentStatus.canceled ||
          orderInfo.order.status === OrderPaymentStatus.paidAndCanceled)
      ) {
        removeFromStorage(STORAGE_KEYS.orderId)
        dispatch(resetOrder())
        dispatch(resetCart())
        dispatch(requestFailure())
        return
      }

      const sortedOrder = sortOrderItemsFromModule(orderInfo)

      dispatch(getOrderSuccess(sortedOrder))
      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const getOrderClean =
  (id: string | number, discardCancelledOrder = false): AppThunk =>
  async (dispatch) => {
    dispatch(requestStart())

    try {
      const response = await apiClient.getOrderClean(id)
      if (!response.success || !response.data) {
        dispatch(requestFailure())
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return
      }

      const orderInfo = response.data

      if (
        discardCancelledOrder &&
        (orderInfo.order.status === OrderPaymentStatus.canceled ||
          orderInfo.order.status === OrderPaymentStatus.paidAndCanceled)
      ) {
        removeFromStorage(STORAGE_KEYS.orderId)
        dispatch(resetOrder())
        dispatch(resetCart())
        dispatch(requestFailure())
        return
      }

      const sortedOrder = sortOrderItemsFromModule(orderInfo)

      dispatch(getOrderSuccess(sortedOrder))
      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const getProlongation =
  (id: string | number): AppThunk =>
  async (dispatch) => {
    dispatch(requestStart())

    try {
      const response = await apiClient.getOrderClean(id)
      if (!response.success || !response.data) {
        dispatch(requestFailure())
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return
      }

      const orderInfo = response.data

      const sortedOrder = sortOrderItemsFromModule(orderInfo)

      dispatch(getProlongationSuccess(sortedOrder))
      dispatch(getCartSuccess(sortedOrder))
      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const getReconciliationOrder =
  (id: string | number): AppThunk =>
  async (dispatch) => {
    dispatch(requestStart())

    try {
      const response = await apiClient.getOrderClean(id)
      if (!response.success || !response.data) {
        dispatch(requestFailure())
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return
      }

      const orderInfo = response.data

      const sortedOrder = sortOrderItemsFromModule(orderInfo)

      dispatch(getReconciliationSuccess(sortedOrder))
      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const getOrderItem =
  (id: string | number, discardCancelled = false): AppThunk =>
  async (dispatch) => {
    dispatch(requestStart())

    try {
      const response = await apiClient.getOrderItem(id)
      if (!response.success || !response.data) {
        dispatch(requestFailure())
        dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
        return
      }

      const orderItemInfo = response.data

      if (
        discardCancelled &&
        (orderItemInfo.orderItem.status === OrderPaymentStatus.canceled ||
          orderItemInfo.orderItem.status === OrderPaymentStatus.paidAndCanceled)
      ) {
        dispatch(resetOrderItem())
        dispatch(requestFailure())
        return
      }

      dispatch(getOrderItemSuccess(orderItemInfo))
      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

export const getOrderItemNoRequest =
  (orderItemInfo: OrderItemInfo, discardCancelled = false): AppThunk =>
  async (dispatch) => {
    dispatch(requestStart())

    try {
      if (
        discardCancelled &&
        (orderItemInfo.orderItem.status === OrderPaymentStatus.canceled ||
          orderItemInfo.orderItem.status === OrderPaymentStatus.paidAndCanceled)
      ) {
        dispatch(resetOrderItem())
        dispatch(requestFailure())
        return
      }

      dispatch(getOrderItemSuccess(orderItemInfo))
      dispatch(requestSuccess())
    } catch {
      dispatch(requestFailure())
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
  }

const createOrderItem = async (
  createRequest: AddOrderItemToOrder,
  pathname: string,
  dispatch: ThunkDispatch<any, any, any>,
): Promise<OrderItemInfo | undefined> => {
  const response = await apiClient.createOrderItem(createRequest)
  if (!response.success || !response.data) {
    if (response.error?.errors.some((s) => s.title === 'Item unavailable')) {
      dispatch(displaySnackbar({ message: i18n.t('common:itemUnavailable'), isError: true }))
    } else {
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }

    dispatch(requestFailure())
    return undefined
  }
  const orderItem = response.data
  trackAddToCart(orderItem, pathname)

  dispatch(setCurrentProductPrice(orderItem.orderItem.price))
  return orderItem
}

const updateOrderItem = async (
  id: number,
  updateRequest: UpdateOrderItemInOrder,
  pathname: string,
  dispatch: ThunkDispatch<any, any, any>,
): Promise<OrderItemInfo | undefined> => {
  const response = await apiClient.updateOrderItem(id, updateRequest)
  if (!response.success || !response.data) {
    if (response.error?.errors.some((s) => s.title === 'Item unavailable')) {
      dispatch(displaySnackbar({ message: i18n.t('common:itemUnavailable'), isError: true }))
    } else {
      dispatch(displaySnackbar({ message: i18n.t('error'), isError: true }))
    }
    dispatch(requestFailure())
    return undefined
  }
  const orderItemInfo = response.data
  trackAddToCart(orderItemInfo, pathname)

  dispatch(setCurrentProductPrice(orderItemInfo.orderItem.price))
  return orderItemInfo
}

export const updateCart =
  (
    orderId: number,
    data: UpdateOrder,
  ): AppThunk<Promise<{ order: OrderInfo | undefined; errorCode?: string } | undefined>> =>
  async (dispatch): Promise<{ order: OrderInfo | undefined; errorCode?: string } | undefined> => {
    const orderToken = JSON.parse(getFromStorage(STORAGE_KEYS.orderToken))
    const isAuthenticated = await getIsUserAuthenticated()

    try {
      if (orderToken || isAuthenticated) {
        data.orderToken = orderToken
        const response = await apiClient.updateOrder(orderId, data)
        if (!response.success) {
          const error = response.error?.errors?.[0]
          if (error) {
            return { order: undefined, errorCode: error.type }
          }
          return undefined
        }

        const orderInfo = response.data
        if (!orderInfo) {
          removeFromStorage(STORAGE_KEYS.orderId)
          dispatch(resetCart())
          return undefined
        }

        const sortedOrderInfo = sortOrderItemsFromModule(orderInfo)
        dispatch(getCartSuccess(sortedOrderInfo))

        return { order: sortedOrderInfo }
      } else {
        return undefined
      }
    } catch {
      return undefined
    }
  }
